<template>
  <div class="image-upload-container">
    <div
      @click="triggerFileInput"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
      class="upload-area"
      :class="{ 'dragging': isDragging, 'has-image': previewUrl }"
    >
      <!-- Preview Image -->
      <div v-if="previewUrl" class="image-preview">
        <img :src="previewUrl" :alt="alt || 'Preview'" class="preview-img" />
        <div class="image-overlay">
          <button
            v-if="!hideDelete"
            @click.stop="removeImage"
            type="button"
            class="delete-btn"
            title="Remove image"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
          <button
            @click.stop="triggerFileInput"
            type="button"
            class="change-btn"
            title="Change image"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
          </button>
        </div>
      </div>

      <!-- Upload Placeholder -->
      <div v-else class="upload-placeholder">
        <svg class="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
        </svg>
        <p class="upload-text">{{ placeholder || 'Click to upload or drag and drop' }}</p>
        <p class="upload-hint">{{ hint || 'PNG, JPG, GIF, WebP up to 5MB' }}</p>
      </div>

      <!-- Hidden File Input -->
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        @change="handleFileSelect"
        class="hidden-input"
      />
    </div>

    <!-- Error Message -->
    <p v-if="error" class="error-message">{{ error }}</p>

    <!-- Upload Progress -->
    <div v-if="uploading" class="progress-container">
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: progress + '%' }"></div>
      </div>
      <p class="progress-text">Uploading... {{ progress }}%</p>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: [String, File],
    default: null
  },
  accept: {
    type: String,
    default: 'image/jpeg,image/png,image/jpg,image/gif,image/webp'
  },
  maxSize: {
    type: Number,
    default: 5 * 1024 * 1024 // 5MB
  },
  placeholder: String,
  hint: String,
  alt: String,
  hideDelete: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'change', 'error'])

const fileInput = ref(null)
const previewUrl = ref(null)
const isDragging = ref(false)
const error = ref(null)
const uploading = ref(false)
const progress = ref(0)

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  if (typeof newValue === 'string' && newValue) {
    previewUrl.value = newValue
  } else if (!newValue) {
    previewUrl.value = null
  }
}, { immediate: true })

const triggerFileInput = () => {
  fileInput.value?.click()
}

const validateFile = (file) => {
  error.value = null

  if (!file) {
    return false
  }

  // Check file type
  const acceptedTypes = props.accept.split(',').map(t => t.trim())
  if (!acceptedTypes.includes(file.type)) {
    error.value = 'Invalid file type. Please upload an image.'
    emit('error', error.value)
    return false
  }

  // Check file size
  if (file.size > props.maxSize) {
    const maxSizeMB = (props.maxSize / (1024 * 1024)).toFixed(1)
    error.value = `File size exceeds ${maxSizeMB}MB limit.`
    emit('error', error.value)
    return false
  }

  return true
}

const handleFileSelect = (event) => {
  const file = event.target.files[0]
  processFile(file)
}

const handleDrop = (event) => {
  isDragging.value = false
  const file = event.dataTransfer.files[0]
  processFile(file)
}

const processFile = (file) => {
  if (!validateFile(file)) {
    return
  }

  // Create preview
  const reader = new FileReader()
  reader.onload = (e) => {
    previewUrl.value = e.target.result
  }
  reader.readAsDataURL(file)

  // Emit the file
  emit('update:modelValue', file)
  emit('change', file)
}

const removeImage = () => {
  previewUrl.value = null
  if (fileInput.value) {
    fileInput.value.value = ''
  }
  emit('update:modelValue', null)
  emit('change', null)
}

// Expose methods for parent components
defineExpose({
  removeImage,
  setProgress: (value) => {
    progress.value = value
  },
  setUploading: (value) => {
    uploading.value = value
  }
})
</script>

<style scoped>
.image-upload-container {
  width: 100%;
}

.upload-area {
  position: relative;
  border: 2px dashed #d1d5db;
  border-radius: 0.75rem;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #f9fafb;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-area:hover {
  border-color: #6366f1;
  background-color: #eef2ff;
}

.upload-area.dragging {
  border-color: #6366f1;
  background-color: #eef2ff;
  transform: scale(1.02);
}

.upload-area.has-image {
  padding: 0;
  border: none;
  background-color: transparent;
  min-height: auto;
}

.image-preview {
  position: relative;
  width: 100%;
  border-radius: 0.75rem;
  overflow: hidden;
}

.preview-img {
  width: 100%;
  height: auto;
  max-height: 400px;
  object-fit: cover;
  display: block;
  border-radius: 0.75rem;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-preview:hover .image-overlay {
  opacity: 1;
}

.delete-btn,
.change-btn {
  padding: 0.75rem;
  background-color: white;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-btn {
  color: #ef4444;
}

.delete-btn:hover {
  background-color: #fee2e2;
  transform: scale(1.1);
}

.change-btn {
  color: #6366f1;
}

.change-btn:hover {
  background-color: #eef2ff;
  transform: scale(1.1);
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.upload-icon {
  width: 3rem;
  height: 3rem;
  color: #9ca3af;
  margin-bottom: 0.5rem;
}

.upload-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin: 0;
}

.upload-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
}

.hidden-input {
  display: none;
}

.error-message {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #ef4444;
}

.progress-container {
  margin-top: 1rem;
}

.progress-bar {
  width: 100%;
  height: 0.5rem;
  background-color: #e5e7eb;
  border-radius: 9999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  transition: width 0.3s ease;
  border-radius: 9999px;
}

.progress-text {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
  text-align: center;
}
</style>
